import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { useState, useEffect } from 'react';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import BreadCrumbs from 'aquilla/core/molecules/Breadcrumbs';
import P1 from '@base/P1/P1';
import { useIsMobile } from '@stores/AppCommonData';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { segment } from '@actions/analytics';
import useLessonPlayListDetails from './useLessonPlayListDetails';
import Header from './Header';
import LessonCard from './LessonCard';
import getFreeClassPlayListBreadcrumbs, { getAuthorName } from './utils';
import useLessonDetails from './useLessonDetails';
import AppInstallDialog from './AppInstallDialog';
import VideoCard from './VideoCard';
import {
  CLASS_CARD,
  PLAYLIST_DETAILS_PAGE,
  PLAYLIST_START_WATCHING_CLICKED,
  PLAY_BUTTON
} from './constants/events';

const Container = styled.div`
  width: 100%;
  background: var(--color-base-1);
  ${mediaQuery.uptoMobile} {
    overflow: hidden;
  }
`;
const HeaderContainer = styled.div`
  width: 100%;
  padding-top: ${Spacings.SPACING_10B};
  border-bottom: ${Spacings.SPACING_1} solid var(--color-divider);
  ${mediaQuery.uptoMobile} {
    padding-top: ${Spacings.SPACING_6B};
  }
`;

const HeaderWrapper = styled.div`
  margin: auto;
  margin-top: ${Spacings.SPACING_0B};
  margin-bottom: ${Spacings.SPACING_6B};
  padding: ${Spacings.SPACING_4B};
  width: 100%;
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_7B};
  }
`;

const Content = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: grid;
  margin-top: ${Spacings.SPACING_10B};
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  padding-bottom: ${Spacings.SPACING_25B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-template-columns: auto;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const LeftContainer = styled.div`
  grid-column: span 16;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const RightContainer = styled.div`
  grid-column: 17/25;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const StyBreadCrumbs = styled(BreadCrumbs)`
  margin: auto;
  margin-bottom: ${Spacings.SPACING_6B};
  width: 1136px;
`;

const LessonCount = styled(P1)`
  color: var(--color-i-black);
  font-size: 20px;
  font-weight: ${Fonts.HEAVY};
`;

const LessonList = ({ playlistUID, goalUID, slug }) => {
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { goalInfo } = useGoalInfo(goalUID);
  const { learnerMeta, lessonDetails } = useLessonDetails(playlistUID);
  const { lessonPlaylistDetails } = useLessonPlayListDetails(playlistUID);
  const isMobile = useIsMobile();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isRequestingLessonPreview, setIsRequestingLessonPreview] =
    useState(false);
  const handleEventSend = (eventProperties = {}) => {
    const author = lessonDetails?.authors?.[0] || {};
    const authorFullName = getAuthorName(author);
    segment.track(PLAYLIST_START_WATCHING_CLICKED, {
      current_screen: PLAYLIST_DETAILS_PAGE,
      goal_uid: goalUID,
      is_new_user: 'NA',
      playlist_name: lessonDetails?.name ?? 'NA',
      playlist_uid: lessonDetails?.uid ?? 'NA',
      educator_uid: author?.uid ?? 'NA',
      educator_name: authorFullName,
      ...eventProperties
    });
  };

  const handlePlayCTA = (eventProperties) => {
    if (eventProperties) {
      handleEventSend(eventProperties);
    }
    if (!isLoggedIn) {
      setIsRequestingLessonPreview(true);
      return openLoginDrawer();
    }
    const { lastWatchedLessonUid } = learnerMeta;
    if (isMobile) {
      setIsModelOpen(true);
      return null;
    }
    if (lastWatchedLessonUid) {
      window.open(`/class/-/${lastWatchedLessonUid}`, '_blank');
    } else {
      const { results } = lessonPlaylistDetails;
      const { slug: classSlug, uid } =
        results?.[0]?.nextSession?.properties || {};
      window.open(`/class/${classSlug}/${uid}`, '_blank');
    }
    setIsRequestingLessonPreview(false);
  };

  useEffect(() => {
    if (isLoggedIn && isRequestingLessonPreview) handlePlayCTA();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleVideoCardClick = () => {
    handleEventSend({
      last_primary_source_section: CLASS_CARD,
      CTA_name: PLAY_BUTTON
    });
  };

  return (
    <Container>
      <HeaderContainer>
        <HeaderWrapper>
          <StyBreadCrumbs
            fragments={getFreeClassPlayListBreadcrumbs(
              goalInfo,
              slug,
              playlistUID
            )}
          />
          <Header
            playlistUID={playlistUID}
            goalUID={goalUID}
            handlePlayCTA={handlePlayCTA}
            slug={slug}
          />
        </HeaderWrapper>
      </HeaderContainer>
      <Content>
        <LeftContainer>
          <LessonCount>{lessonDetails?.lessonsCount} Lessons</LessonCount>
          {lessonPlaylistDetails?.results?.map((videoItem, index) => (
            <VideoCard
              key={videoItem?.uid}
              video={videoItem}
              topicName={lessonDetails?.topicGroupName}
              lessonNumber={index + 1}
              isLoggedIn={isLoggedIn}
              handleVideoCardClick={handleVideoCardClick}
            />
          ))}
        </LeftContainer>
        <RightContainer>
          <LessonCard
            playlistUID={playlistUID}
            goalUID={goalUID}
            handlePlayCTA={handlePlayCTA}
          />
        </RightContainer>
      </Content>
      <AppInstallDialog
        isModelOpen={isModelOpen}
        setIsModelOpen={setIsModelOpen}
      />
    </Container>
  );
};

export default LessonList;
